// eslint-disable-next-line import/no-extraneous-dependencies
import confetti from 'canvas-confetti';
import { useCallback } from 'react';

const confettiColors = [
  '#FFD700',
  '#DAA520',
  '#C0C0C0',
  '#D9D9D9',
];

const crossfireConfettiOptions = {
  particleCount: 100,
  spread: 50,
  startVelocity: 50,
  gravity: 0.5,
  ticks: 50,
  scalar: 1.3,
  colors: confettiColors,
};

const leftCrossfireConfettiOptions = {
  ...crossfireConfettiOptions,
  angle: 60,
  origin: { x: 0, y: 1 },
};

const rightCrossfireConfettiOptions = {
  ...crossfireConfettiOptions,
  angle: 120,
  origin: { x: 1, y: 1 },
};

interface FireworksConfettiOptions extends confetti.Options {
  duration: number;
  interval: number;
}

const fireworksOptions: FireworksConfettiOptions = {
  startVelocity: 30,
  spread: 360,
  ticks: 60,
  zIndex: 0,
  colors: confettiColors,
  duration: 10000,
  interval: 250,
};

const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

const randomSize = () => 1.3 + randomInRange(0.1, 0.2);

const randomLifetime = () => Math.floor(randomInRange(45, 75));

export const useConfetti = () => {
  const launchCrossfireConfetti = useCallback((options?: confetti.Options) => {
    const left = confetti({
      ...leftCrossfireConfettiOptions,
      ...options,
    });

    const right = confetti({
      ...rightCrossfireConfettiOptions,
      ...options,
    });

    return Promise.all([left, right]);
  }, []);

  const launchFireworks = useCallback(({
    duration = fireworksOptions.duration,
    interval = fireworksOptions.interval,
    ...customFireworksOptions
  }: Partial<FireworksConfettiOptions> = {}) => {
    const animationEnd = Date.now() + duration;

    const fireworksInterval: NodeJS.Timeout = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(fireworksInterval);

        return;
      }

      const particleCount = 100 * (timeLeft / duration);

      confetti({
        ...fireworksOptions,
        ...customFireworksOptions,
        particleCount,
        scalar: randomSize(),
        ticks: randomLifetime(),
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });

      confetti({
        ...fireworksOptions,
        ...customFireworksOptions,
        particleCount,
        scalar: randomSize(),
        ticks: randomLifetime(),
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, interval);

    return fireworksInterval;
  }, []);

  return { launchCrossfireConfetti, launchFireworks };
};
